import type {AgencyInviteSerializer, SubBrandListSerializer} from "@/generated/DjangoTypes";
import {useGenericInfiniteQuery, useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import type {TCreateAgencyFormState} from "@/store/createAgency";
import type {TAccountMember, TAgency, TInfiniteQueryOptions, TPaymentMethod, TQueryOptions} from "@/store/types";
import {useMutation, type UseMutationOptions} from "@tanstack/react-query";

export const AGENCY_KEYS = {
  mAgency: (agencyId: number, params = "") => [`/agency/${agencyId}/${params ? `?${params}` : ""}`],
  mAgencyBrandsMerge: ["/agency/merge_brands/"],
  mAgencyCreateSetupIntent: (agencyId: number) => [`/agency/${agencyId}/create_setup_intent/`],
  mAgencyDeleteAccount: (agencyId: number) => [`/agency/${agencyId}/delete_account/`],
  mAgencyInvite: (agencyId: number) => [`/agency/${agencyId}/invite_user/`],
  mAgencyMemberInviteRemoval: (agencyId: number) => [`/agency/${agencyId}/remove_invite/`],
  mAgencyMemberRegistration: (agencyId: number) => [`/agency/${agencyId}/register_agency_user/`],
  mAgencyMemberRemoval: (agencyId: number) => [`/agency/${agencyId}/remove_user/`],
  mAgencyPaymentMethodAttach: (agencyId: number) => [`/agency/${agencyId}/attach_payment_card/`],
  mAgencyPaymentMethodDefault: (agencyId: number) => [`/agency/${agencyId}/set_default_payment_method/`],
  mAgencyPaymentMethodDetach: (agencyId: number) => [`/agency/${agencyId}/detach_payment_card/`],
  mAgencyPreRegister: ["/agency/pre_register/"],
  mAgencyRegister: ["/agency/register/"],
  mAgencySetOwner: (agencyId: number) => [`/agency/${agencyId}/set_owner/`],
  mAgencySubBrandRegister: (agencyId: number) => [`/agency/${agencyId}/add_sub_brand/`],
  qAgency: (agencyId: number) => [`/agency/${agencyId}/`],
  qAgencyAmbassadors: (agencyId: number) => [`/agency/${agencyId}/ambassadors/`],
  qAgencyCampaigns: (params?: {
    agencyId?: number;
    status: "active" | "complete" | "drafts" | "paused" | "planned" | "open";
  }) => [`/agency/${params?.agencyId}/${params?.status ?? "all"}/`],
  qAgencyInvites: (agencyId: number) => [`/agency/${agencyId}/invites/`],
  qAgencyPaymentMethods: (agencyId: number) => [`/agency/${agencyId}/payment_cards/`],
  qAgencyList: (params: string) => [`/agency/?${params}`],
  qAgencyMergeBrandsPreview: (selectedCompanyIds: number[]) => [
    `/agency/merge_brands_preview/?company_profile_ids=${selectedCompanyIds.join(",")}`,
  ],
  qAgencyMembers: (agencyId: number) => [`/agency/${agencyId}/members/`],
  qAgencyPreRegisterDetails: (uuid: string) => [`/agency/pre_register_details/?uuid=${uuid}`],
  qAgencyRecommendedInfluencers: (agencyId: number) => [`/agency/${agencyId}/recommended_influencers/`],
  qAgencySubbrands: (agencyId: number) => [`/agency/${agencyId}/sub_brands/`],
} as const;

export const useAgencyInviteMutation = ({
  agencyId,
  email,
  mutationOptions,
}: {
  agencyId: number;
  email: string;
  mutationOptions?: Partial<UseMutationOptions>;
}) =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: AGENCY_KEYS.mAgencyInvite(agencyId),
    mutationFn: () => client.post(AGENCY_KEYS.mAgencyInvite(agencyId)[0], {emails_data: email}),
    ...mutationOptions,
  });

export const useDeleteBrandAccountMutation = ({
  agencyId,
  mutationOptions,
}: {
  agencyId: number;
  mutationOptions?: Partial<UseMutationOptions>;
}) =>
  useMutation({
    mutationKey: AGENCY_KEYS.mAgencyDeleteAccount(agencyId),
    mutationFn: () => client.delete(AGENCY_KEYS.mAgencyDeleteAccount(agencyId)[0]),
    ...mutationOptions,
  });

export const useSetAgencyOwnerMutation = ({agencyId}: {agencyId: number}) =>
  useMutation({
    mutationKey: AGENCY_KEYS.mAgencySetOwner(agencyId),
    mutationFn: ({userId}: {userId: number}) =>
      client.post(AGENCY_KEYS.mAgencySetOwner(agencyId)[0], {user_id: userId}),
    onSuccess: () => {
      [AGENCY_KEYS.qAgency(agencyId), AGENCY_KEYS.qAgencyMembers(agencyId)].map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      );
    },
  });

export const useAgencyQuery = ({agencyId, queryOptions}: {agencyId: number; queryOptions?: TQueryOptions<TAgency>}) =>
  useGenericQuery<TAgency>({
    queryKey: AGENCY_KEYS.qAgency(agencyId),
    enabled: !!agencyId,
    ...queryOptions,
  });

export const useAgencyPaymentMethodsQuery = ({
  agencyId,
  queryOptions,
}: {
  agencyId?: number;
  queryOptions?: TQueryOptions<TPaymentMethod[]>;
}) =>
  useGenericQuery<TPaymentMethod[]>({
    queryKey: AGENCY_KEYS.qAgencyPaymentMethods(agencyId ?? 0),
    enabled: agencyId !== undefined && !Number.isNaN(agencyId),
    ...queryOptions,
  });

export const useAgencyInvitesQuery = ({
  agencyId,
  queryOptions,
}: {
  agencyId: number;
  queryOptions?: TQueryOptions<AgencyInviteSerializer[]>;
}) =>
  useGenericQuery<AgencyInviteSerializer[]>({
    queryKey: AGENCY_KEYS.qAgencyInvites(agencyId),
    enabled: !!agencyId,
    ...queryOptions,
  });

export const useAgencyMembersQuery = ({
  agencyId,
  queryOptions,
}: {
  agencyId: number;
  queryOptions?: TInfiniteQueryOptions<TAccountMember>;
}) =>
  useGenericInfiniteQuery<TAccountMember>({
    queryKey: AGENCY_KEYS.qAgencyMembers(agencyId),
    enabled: !!agencyId,
    ...queryOptions,
  });

export const useAgencyPreRegisterDetailsQuery = ({uuid}: {uuid: string}) =>
  useGenericQuery<TCreateAgencyFormState>({
    queryKey: AGENCY_KEYS.qAgencyPreRegisterDetails(uuid),
    enabled: !!uuid,
  });

export const useAgencySubbrandsQuery = ({
  agencyId,
  queryOptions,
}: {
  agencyId: number;
  queryOptions?: TQueryOptions<SubBrandListSerializer[]>;
}) =>
  useGenericQuery<SubBrandListSerializer[]>({
    queryKey: AGENCY_KEYS.qAgencySubbrands(agencyId),
    enabled: !!agencyId,
    ...queryOptions,
  });
